import { useDispatch } from 'react-redux';
import { Payment } from '@mews-commander/types';
import {
    ConvertedCurrencyValue,
    FlexAlignCenter,
    FlexSpaceBetween,
    SpacingSmallLeft,
    SpacingSmallRight,
} from '@mews-commander/ui';
import { Checkbox, Stack, Tag } from '@optimus-web/core';
import { PaymentType, Payment as PaymentDTO, InvoicePaymentType } from '@mews-commander/data-access';
import { LocalizedText } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { getNegativeAmount, getPositiveAmount, isAmountPositive, useCommanderSelector } from '@mews-commander/utils';
import { useCurrentEnterpriseId } from '@mews-commander/core';
import { getPayment } from '@mews-commander/legacy-entities/src/ducks';
import { isAccountIdCompany } from '@mews-commander/legacy-entities/src/selectors/bills';
import { BillListItem } from '../../../../components';
import { isPaymentSelected, togglePaymentSelect } from '../../../../ducks/billItems';
import { PaymentItemMenu } from './PaymentItemMenu';
import { PaymentTypeName } from './PaymentTypeName';
import { getPaymentStateTagProps } from './utils';

interface PaymentItemProps {
    payment: Payment;
    selected: boolean;
    toggleSelect(): void;
}

const usePaymentText = (payment: Payment) => {
    const { value, data, id, ownerId, serviceOrderId, billId } = payment;
    const { textKey, variant } = getPaymentStateTagProps(payment);

    const isRefund = isAmountPositive(value);
    const amount = isRefund ? getNegativeAmount(value) : getPositiveAmount(value);

    const isCompany = useCommanderSelector((state) => isAccountIdCompany(state, { accountId: ownerId }));
    const isInvoiceOverpaid =
        data.discriminator === PaymentType.Invoice && data.value.type === InvoicePaymentType.Overpayment;

    return (
        <FlexSpaceBetween>
            <FlexAlignCenter>
                {data.discriminator !== PaymentType.Invoice ? (
                    <SpacingSmallRight>
                        <Tag variant={variant}>
                            <LocalizedText textKey={textKey} />
                        </Tag>
                    </SpacingSmallRight>
                ) : null}
                {isInvoiceOverpaid ? (
                    <SpacingSmallRight>
                        <Tag variant="primary">
                            <LocalizedText textKey={TextKey.Overpaid} />
                        </Tag>
                    </SpacingSmallRight>
                ) : null}
                <PaymentTypeName payment={payment as unknown as PaymentDTO} />
                <SpacingSmallLeft />
            </FlexAlignCenter>
            <Stack itemAlignment="center" spacing="200">
                <ConvertedCurrencyValue amount={amount} />
                {!isCompany ? (
                    // This prevents clicks in the Menu DOM tree to bubble up to the row element and toggling selected state on the row
                    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
                    <span onClick={(e) => e.stopPropagation()}>
                        <PaymentItemMenu
                            paymentId={id}
                            accountId={ownerId}
                            billId={billId}
                            serviceOrderId={serviceOrderId ?? undefined}
                        />
                    </span>
                ) : null}
            </Stack>
        </FlexSpaceBetween>
    );
};

export const PaymentItem: React.FC<PaymentItemProps> = ({ payment, selected, toggleSelect }) => (
    <BillListItem
        primaryText={usePaymentText(payment)}
        leftItem={<Checkbox checked={selected} />}
        onClick={toggleSelect}
        data-test-id={`bill-list-item-${payment.id}`}
        data-test-selected={selected}
    />
);

export const PaymentItemLinkedToReservation = ({
    paymentId,
    scope,
    depth,
}: {
    paymentId: string;
    scope: string;
    depth: number;
}) => {
    const dispatch = useDispatch();

    const enterpriseId = useCurrentEnterpriseId();
    const payment = useCommanderSelector((state) => getPayment(state, { id: paymentId, enterpriseId }));
    // @ts-expect-error $TSFixMeError: Argument of type '{ paymentId: string; scope: string; }' is not assignable to parameter of type '{ paymentId: string; }'...
    const selected = useCommanderSelector((state) => isPaymentSelected(state, { paymentId, scope }));

    const toggleSelect = () => dispatch(togglePaymentSelect(paymentId, scope));

    return (
        <BillListItem
            primaryText={usePaymentText(payment as Payment)}
            leftItem={<Checkbox checked={selected} />}
            onClick={toggleSelect}
            depth={depth}
        />
    );
};
